"use client";

import Link from "next/link";
import { Suspense, use, useEffect, useMemo, useRef, useState } from "react";

import { SegnoTitolo } from "@/components/home/TestaSezione";
import { FUOCO } from "@/components/ui/pulsanti";
import { ConEvidenza, RigaRisultato } from "@/components/ricerca/RigaRisultato";
import { PAROLE_TIPO, hrefRullo, it, perGiorno, type RigaRullo, type StatoRullo } from "@/lib/rullo";
import type { ReportInEvidenza } from "@/lib/ricerca-pagina";

type Props = {
  stato: StatoRullo;
  /** `?ordine=pertinenti`: i soli dispacci con la parola nel titolo. */
  pertinenti: boolean;
  iniziali: RigaRullo[];
  /** `null` con un testo cercato: il totale arriva dopo, in `conteggio`. */
  totale: number | null;
  /** Il conteggio chiesto dal server in parallelo alla lista (una promessa
   *  che attraversa il confine server → client e si legge con `use`). */
  conteggio: Promise<number | null> | null;
  /** Con un testo è un limite inferiore («ce n'è almeno un'altra»): la
   *  aggiorna ogni blocco che arriva scorrendo. */
  pagine: number;
  evidenza: ReportInEvidenza | null;
  /** I dispacci da mostrare nel bianco sotto una lista corta (vedi
   *  `codaDellaLista` in app/rullo/page.tsx); `null` se la lista non è corta. */
  coda?: CodaRisultati | null;
};

/** La coda di una lista corta: altri dispacci, dichiarati a parte. */
export type CodaRisultati = {
  titolo: string;
  nota: string;
  rimando: { href: string; etichetta: string } | null;
  righe: RigaRullo[];
};

/** Il totale quando il conteggio arriva; «—» se è caduto. */
function TotaleInArrivo({ p }: { p: Promise<number | null> }) {
  const n = use(p);
  return <>{n === null ? "—" : it(n)}</>;
}

const SEG = "border px-3 py-[7px] font-sans text-[13px] font-medium no-underline";
const SEG_OFF = "border-rule bg-surface text-sommesso";
const SEG_ON = "border-mare bg-mare text-suMare";

/** L'indirizzo della pagina con l'ordine, che `hrefRullo` non conosce. */
function conOrdine(href: string, pertinenti: boolean): string {
  if (!pertinenti) return href;
  return href + (href.includes("?") ? "&" : "?") + "ordine=pertinenti";
}

/**
 * La colonna dei risultati di /ricerca (`.rc-feed` del modello del
 * 20/10/2026): testata «Risultati per «…»» col conteggio, la barra Ordina
 * · Vista · Salva, il report in evidenza, i dispacci per giorno e
 * «Mostra altri risultati», che li aggiunge sotto senza cambiare pagina.
 *
 * - Vista «Solo titoli» è CSS (`.oi-rc-feed:has(#rc-vista:checked)` in
 *   globals.css): nessuno stato, niente salti all'idratazione.
 * - Ordina: «Più recenti» è l'ordine del registro; «Più pertinenti» non
 *   è un punteggio (a monte non c'è), è la stessa ricerca ristretta ai
 *   dispacci con la parola nel titolo, sempre dal più recente. Sta
 *   nell'indirizzo (`?ordine=pertinenti`).
 * - «Salva questa ricerca» copia il suo indirizzo: il sito non ha conti
 *   personali in cui salvarla, e l'indirizzo contiene tutta la ricerca.
 * - Lo scorrimento è continuo: in fondo alla lista una sentinella chiede
 *   da sola il blocco dopo; il pulsante resta come ripiego.
 * - «Mostra altri risultati» chiede la pagina dopo al proxy
 *   (`/api/cases?formato=registro&perimetro=archivio`, le stesse righe del
 *   server); senza JavaScript è un link alla pagina dopo.
 */
export function ElencoRisultati({ stato, pertinenti, iniziali, totale, conteggio, pagine: pagineIniziali, evidenza, coda }: Props) {
  const q = stato.q.trim();
  const [altre, setAltre] = useState<RigaRullo[]>([]);
  const [pagina, setPagina] = useState(stato.page);
  const [carico, setCarico] = useState<"fermo" | "caricando" | "errore">("fermo");
  const [salvata, setSalvata] = useState(false);
  const inVolo = useRef(false);
  /* Quante pagine ci sono, per quanto se ne sa: senza testo è il numero
     vero dalla prima risposta; con un testo è un limite che ogni blocco
     arrivato spinge avanti (vedi `paginaRegistro`). */
  const [pagine, setPagine] = useState(pagineIniziali);

  const righe = useMemo(() => {
    const visti = new Set<string>();
    return [...iniziali, ...altre].filter((r) => (visti.has(r.id) ? false : (visti.add(r.id), true)));
  }, [iniziali, altre]);
  const giorni = perGiorno(righe);
  const finito = pagina >= pagine;

  /* La pagina dopo, dal proxy. La leggono il pulsante (ripiego) e lo
     scorrimento: `pagina` passa da un ref perché l'osservatore qui sotto
     è montato una volta sola e altrimenti leggerebbe sempre la prima. */
  const paginaRef = useRef(pagina);
  function carica() {
    if (inVolo.current || paginaRef.current >= pagine) return;
    inVolo.current = true;
    setCarico("caricando");
    const href = hrefRullo(stato, { page: paginaRef.current + 1 });
    const qs = href.includes("?") ? href.slice(href.indexOf("?") + 1) : "";
    fetch(`/api/cases?formato=registro&perimetro=archivio${pertinenti ? "&ordine=pertinenti" : ""}&${qs}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { righe: RigaRullo[]; pagine?: number }) => {
        setAltre((a) => [...a, ...d.righe]);
        if (typeof d.pagine === "number") setPagine((p) => Math.max(p, d.pagine!));
        // Un blocco vuoto chiude la lista invece di chiedere pagine vuote:
        // il numero di pagine si ferma a quella già letta.
        if (d.righe.length) setPagina((p) => p + 1);
        else setPagine(paginaRef.current);
        setCarico("fermo");
      })
      .catch(() => setCarico("errore"))
      .finally(() => {
        inVolo.current = false;
      });
  }
  const caricaRef = useRef(carica);
  useEffect(() => {
    paginaRef.current = pagina;
    caricaRef.current = carica;
  });

  function altri(e: React.MouseEvent) {
    e.preventDefault();
    carica();
  }

  /* Lo scorrimento continuo, come il Rullo (richiesta del committente del
     20/10/2026): quando la sentinella in fondo alla lista arriva a 1.500
     pixel dalla finestra si chiede il blocco dopo. Il margine è largo
     perché una pagina nuova a monte costa qualche secondo (la ricerca nel
     testo di tutto l'archivio): così di solito arriva prima che chi legge
     tocchi il fondo. Dopo un errore si ferma, e resta il pulsante per
     riprovare. */
  const sentinella = useRef<HTMLDivElement>(null);
  const fermo = carico === "errore";
  useEffect(() => {
    const el = sentinella.current;
    if (!el || fermo) return;
    const io = new IntersectionObserver((voci) => {
      if (voci.some((v) => v.isIntersecting)) caricaRef.current();
    }, { rootMargin: "0px 0px 1500px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [fermo, pagina]);

  function salva() {
    navigator.clipboard?.writeText(window.location.href).then(
      () => setSalvata(true),
      () => setSalvata(false)
    );
  }

  const base = hrefRullo(stato, { page: 1 });

  return (
    /* `flex-col`: con una coda la sezione è alta quanto la colonna destra
       (la griglia la stira) e la coda prende il resto (`flex-1`). Il
       contenuto di prima sta in un blocco suo, così i margini fra testata,
       barra e giorni si comportano come prima. */
    <section aria-labelledby="rc-risultati" className="oi-rc-feed flex min-w-0 flex-col">
      <div>
      <input id="rc-vista" type="checkbox" className="sr-only" aria-label="Solo titoli" />
      <h2
        id="rc-risultati"
        className="m-0 mb-[18px] flex items-baseline gap-2.5 border-b border-ink pb-2.5 font-serif text-[26px] font-normal text-ink"
      >
        <SegnoTitolo />
        {q ? <>Risultati per «{q}»</> : PAROLE_TIPO[stato.filone].tutti}
        <span className="ml-auto font-mono text-meta text-sommesso">
          {it(righe.length)} di{" "}
          {totale !== null ? (
            it(totale)
          ) : conteggio ? (
            <Suspense fallback="…">
              <TotaleInArrivo p={conteggio} />
            </Suspense>
          ) : (
            "—"
          )}
        </span>
      </h2>

      <div className="-mt-1.5 mb-[18px] flex flex-wrap items-center gap-x-[22px] gap-y-2.5">
        {q ? (
          <div className="flex items-center">
            <span className="mr-2.5 font-sans text-voce font-semibold text-sommesso">Ordina</span>
            <Link href={base} aria-current={!pertinenti ? "true" : undefined} className={`${SEG} ${!pertinenti ? SEG_ON : SEG_OFF} ${FUOCO}`}>
              Più recenti
            </Link>
            <Link
              href={conOrdine(base, true)}
              aria-current={pertinenti ? "true" : undefined}
              className={`${SEG} -ml-px ${pertinenti ? SEG_ON : SEG_OFF} ${FUOCO}`}
            >
              Più pertinenti
            </Link>
          </div>
        ) : null}
        <div className="flex items-center">
          <span className="mr-2.5 font-sans text-voce font-semibold text-sommesso">Vista</span>
          <label htmlFor="rc-vista" className={`oi-rc-v1 cursor-pointer ${SEG} ${SEG_OFF}`}>
            Estesa
          </label>
          <label htmlFor="rc-vista" className={`oi-rc-v2 -ml-px cursor-pointer ${SEG} ${SEG_OFF}`}>
            Solo titoli
          </label>
        </div>
        <button
          type="button"
          onClick={salva}
          className={`ml-auto inline-flex cursor-pointer items-center gap-2 border border-ink bg-transparent px-3.5 py-2 font-sans text-[13px] font-semibold text-ink hover:border-segnale hover:text-segnale max-[900px]:ml-0 ${FUOCO}`}
        >
          <svg viewBox="0 0 11 13" aria-hidden className="h-[13px] w-[11px] fill-none stroke-segnale" strokeWidth={1.4}>
            <path d="M1 1h9v11L5.5 9 1 12z" />
          </svg>
          {salvata ? "Indirizzo copiato" : "Salva questa ricerca"}
        </button>
        <span role="status" className="sr-only">
          {salvata ? "L’indirizzo della ricerca è stato copiato." : ""}
        </span>
      </div>

      {pertinenti ? (
        <p className="m-0 mb-4 font-sans text-[13px] text-sommesso">
          Più pertinenti: i dispacci con «{q}» nel titolo, dal più recente.
        </p>
      ) : null}

      {/* I report della selezione (dal 25/10/2026 con ogni filtro, non solo
          con una parola): il più recente in grande, i due dopo in una riga,
          il rimando a tutti. Solo in testa alla prima pagina. */}
      {evidenza && pagina === stato.page && stato.page === 1 ? (
        <div className="oi-rc-evidenza mb-1.5 flex flex-col border border-l-[3px] border-rule border-l-segnale">
          <Link
            href={evidenza.href}
            className={`flex flex-col gap-1.5 px-5 py-[18px] text-ink no-underline ${FUOCO}`}
          >
            <span className="font-mono text-[11px] font-medium tracking-[0.06em] text-sommesso">
              <span className="mr-2.5 font-sans text-[13px] font-semibold tracking-normal text-segnale">
                {evidenza.totale > 1 ? "Report in evidenza" : "Il report"}
              </span>
              {[evidenza.paese, evidenza.categoria, evidenza.codice].filter(Boolean).join(" · ").toUpperCase()}
            </span>
            <span className="oi-rc-evidenza-t font-serif text-[24px] leading-[1.2]">
              <ConEvidenza testo={evidenza.titolo} q={q} />
            </span>
            <span className="line-clamp-2 font-sans text-[14.5px] leading-[1.5] text-sommesso">{evidenza.sommario}</span>
            <span className="font-sans text-[13.5px] font-semibold text-mare">
              Apri il report <span aria-hidden>↗</span>
            </span>
          </Link>
          {evidenza.altri.length ? (
            <ul className="m-0 list-none border-t border-rule p-0">
              {evidenza.altri.map((r) => (
                <li key={r.href} className="border-b border-rule last:border-b-0">
                  <Link
                    href={r.href}
                    className={`grid grid-cols-1 gap-x-4 gap-y-0.5 px-5 py-3 text-ink no-underline hover:text-mare min-[640px]:grid-cols-[minmax(0,1fr)_auto] ${FUOCO}`}
                  >
                    <span className="font-serif text-[17px] leading-[1.3]">
                      <ConEvidenza testo={r.titolo} q={q} />
                    </span>
                    <span className="font-mono text-[11px] tracking-[0.06em] text-sommesso uppercase min-[640px]:pt-1">
                      {r.data} · {r.codice}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
          {evidenza.totale > 1 + evidenza.altri.length ? (
            <Link
              href={evidenza.tuttiHref}
              className={`border-t border-rule px-5 py-3 font-sans text-[13.5px] font-semibold text-mare no-underline hover:underline ${FUOCO}`}
            >
              Tutti i {it(evidenza.totale)} report di questa selezione <span aria-hidden>↗</span>
            </Link>
          ) : null}
        </div>
      ) : null}

      {/* La chiave porta anche la posizione: i blocchi aggiunti con «Mostra
          altri» possono riaprire un giorno già visto (un dispaccio entrato a
          monte fra due richieste fa scivolare la lista), e due gruppi dello
          stesso giorno davano chiavi doppie. */}
      {giorni.map((g, n) => (
        <div key={`${g.giorno}-${n}`}>
          {/* La data del gruppo, discreta (richiesta del 20/10/2026: in
              maiuscolo mono neretto era «invasiva»): Geist 12 sommesso, in
              minuscolo come si scrive, il filetto e il conto accanto. */}
          <h3 className="m-0 mt-6 flex items-center gap-3 font-sans text-[12px] font-normal text-sommesso">
            <span>{g.etichetta}</span>
            <i aria-hidden className="h-px flex-1 bg-rule" />
            <span className="font-mono text-[10.5px] tracking-[0.06em]">
              {g.righe.length} {g.righe.length === 1 ? "risultato" : "risultati"}
            </span>
          </h3>
          <ol className="m-0 mt-1.5 list-none p-0">
            {g.righe.map((r) => (
              <RigaRisultato key={r.id} r={r} q={q} />
            ))}
          </ol>
        </div>
      ))}

      <div ref={sentinella} aria-hidden />
      {!finito ? (
        <div className="mt-[22px] flex items-center gap-[18px] border-t border-ink pt-[18px] max-[900px]:flex-col max-[900px]:items-start">
          <a
            href={conOrdine(hrefRullo(stato, { page: pagina + 1 }), pertinenti)}
            onClick={altri}
            aria-disabled={carico === "caricando"}
            className={`border border-ink bg-surface px-5 py-3 font-sans text-[14px] font-semibold text-ink no-underline hover:border-segnale hover:text-segnale ${FUOCO}`}
          >
            {carico === "caricando" ? "Caricamento…" : carico === "errore" ? "Riprova ↻" : "Mostra altri risultati ↓"}
          </a>
          <span className="font-sans text-[13px] text-sommesso">
            {carico === "errore"
              ? "L’archivio non ha risposto: riprova tra qualche istante."
              : carico === "caricando"
                ? "Carico i risultati successivi…"
                : "Scorrendo, i risultati si aggiungono da soli."}
          </span>
        </div>
      ) : null}
      </div>
      {coda && finito && pagina === stato.page ? <CodaElenco coda={coda} q={q} /> : null}
    </section>
  );
}

/** La coda nel bianco sotto una lista corta (26/10/2026). Le righe stanno
 *  in un livello `absolute inset-0` dentro una scatola `flex-1`: l'altezza
 *  la decide la colonna destra, non loro, quindi la coda non allunga mai la
 *  pagina. Dopo l'idratazione si tolgono le righe dal fondo finché tutto sta
 *  intero (un giorno senza righe sparisce con loro); se non ne sta nemmeno
 *  una sparisce tutta la coda. Finché la misura non è fatta il livello è
 *  trasparente: niente riga tagliata a metà in vista, e niente salto,
 *  perché nascondere dentro un livello assoluto non sposta niente. Sotto i
 *  1100 pixel la colonna destra va sotto la lista, la scatola è alta zero e
 *  la coda non si vede: lì il bianco non c'è. */
function CodaElenco({ coda, q }: { coda: CodaRisultati; q: string }) {
  const strato = useRef<HTMLDivElement>(null);
  const [pronta, setPronta] = useState(false);
  const giorni = perGiorno(coda.righe);

  useEffect(() => {
    const el = strato.current;
    if (!el) return;
    const adatta = () => {
      const righe = [...el.querySelectorAll<HTMLElement>("li")];
      const gruppi = [...el.querySelectorAll<HTMLElement>("[data-giorno]")];
      righe.forEach((r) => (r.style.display = ""));
      gruppi.forEach((g) => (g.style.display = ""));
      el.style.display = "";
      let restano = righe.length;
      // `scrollHeight` del livello: quanto chiede il contenuto, contro
      // l'altezza che la colonna gli lascia.
      while (restano > 0 && el.scrollHeight > el.clientHeight + 1) {
        righe[--restano]!.style.display = "none";
        for (const g of gruppi) if (!g.querySelector("li:not([style*='none'])")) g.style.display = "none";
      }
      if (restano === 0) el.style.display = "none";
      setPronta(true);
    };
    adatta();
    const box = el.parentElement!;
    const ro = new ResizeObserver(adatta);
    ro.observe(box);
    document.fonts?.ready.then(adatta);
    // «Solo titoli» accorcia le righe: ne entrano di più.
    const vista = document.getElementById("rc-vista");
    vista?.addEventListener("change", adatta);
    return () => {
      ro.disconnect();
      vista?.removeEventListener("change", adatta);
    };
  }, [coda]);

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={strato}
        className={`absolute inset-0 overflow-hidden transition-opacity duration-300 ${pronta ? "opacity-100" : "opacity-0"}`}
      >
        <div className="mt-10 border-t-2 border-ink pt-3">
          <p className="m-0 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <span className="font-serif text-[22px] leading-[1.2] text-ink">{coda.titolo}</span>
            {coda.rimando ? (
              <Link
                href={coda.rimando.href}
                className={`font-sans text-[13.5px] font-semibold text-mare no-underline hover:underline ${FUOCO}`}
              >
                {coda.rimando.etichetta} <span aria-hidden>↗</span>
              </Link>
            ) : null}
          </p>
          <p className="m-0 mt-1 font-sans text-[13px] text-sommesso">{coda.nota}</p>
        </div>
        {giorni.map((g, n) => (
          <div key={`${g.giorno}-${n}`} data-giorno>
            <h3 className="m-0 mt-6 flex items-center gap-3 font-sans text-[12px] font-normal text-sommesso">
              <span>{g.etichetta}</span>
              <i aria-hidden className="h-px flex-1 bg-rule" />
            </h3>
            <ol className="m-0 mt-1.5 list-none p-0">
              {g.righe.map((r) => (
                <RigaRisultato key={r.id} r={r} q={q} />
              ))}
            </ol>
          </div>
        ))}
      </div>
    </div>
  );
}
